import type { MouseButton, ViewerInputMessage } from '@viewer/protocol';
import { keyboardCaptureSet, onCapturedKey, type CapturedKey } from '../../lib/tauri/keyboard';

/**
 * Converte a posição do ponteiro (coordenadas da janela) para pixels do frame
 * de vídeo, descontando as barras pretas do `object-fit: contain`.
 * Com `region` (um monitor recortado do frame), o resultado fica preso nele.
 * Retorna null se o vídeo ainda não tem dimensões.
 */
export function pointerToFrame(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  frameWidth: number,
  frameHeight: number,
  region?: { x: number; y: number; width: number; height: number },
): { x: number; y: number } | null {
  if (frameWidth <= 0 || frameHeight <= 0 || rect.width <= 0 || rect.height <= 0) return null;
  const scale = Math.min(rect.width / frameWidth, rect.height / frameHeight);
  const shownWidth = frameWidth * scale;
  const shownHeight = frameHeight * scale;
  const offsetX = rect.left + (rect.width - shownWidth) / 2;
  const offsetY = rect.top + (rect.height - shownHeight) / 2;
  const x = (clientX - offsetX) / scale;
  const y = (clientY - offsetY) / scale;
  if (!region) return { x: clamp(x, 0, frameWidth), y: clamp(y, 0, frameHeight) };
  // A borda direita/inferior já é o primeiro pixel do monitor vizinho, que não está à vista.
  return {
    x: clamp(x, region.x, region.x + region.width - 1),
    y: clamp(y, region.y, region.y + region.height - 1),
  };
}

/** O inverso de `pointerToFrame`: onde um pixel do frame aparece dentro de `rect`. */
export function frameToPointer(
  x: number,
  y: number,
  rect: { left: number; top: number; width: number; height: number },
  frameWidth: number,
  frameHeight: number,
): { x: number; y: number } | null {
  if (frameWidth <= 0 || frameHeight <= 0 || rect.width <= 0 || rect.height <= 0) return null;
  const scale = Math.min(rect.width / frameWidth, rect.height / frameHeight);
  return {
    x: rect.left + (rect.width - frameWidth * scale) / 2 + x * scale,
    y: rect.top + (rect.height - frameHeight * scale) / 2 + y * scale,
  };
}

/** A barra de rolagem da imagem ampliada é deste lado: mouse e cliques nela não vão para o Host. */
function onScrollbar(e: PointerEvent) {
  const target = e.target as HTMLElement;
  const scrolls = target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight;
  return scrolls && (e.offsetX >= target.clientWidth || e.offsetY >= target.clientHeight);
}

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

export function pointerButton(button: number): MouseButton | null {
  switch (button) {
    case 0:
      return 'left';
    case 1:
      return 'middle';
    case 2:
      return 'right';
    default:
      return null;
  }
}

/** Normaliza o WheelEvent para "linhas" (um clique de roda ≈ 1 linha, 100px no Chromium). */
export function wheelToLines(delta: number, deltaMode: number): number {
  if (deltaMode === 1) return delta; // DOM_DELTA_LINE
  if (deltaMode === 2) return delta * 3; // DOM_DELTA_PAGE
  return delta / 100;
}

export type SendInput = (message: ViewerInputMessage) => boolean;

/** Ctrl+V na superfície: no app lê a área de transferência; no navegador espera o evento `paste`. */
export function pasteShortcutKind(
  event: { ctrlKey: boolean; altKey: boolean; metaKey: boolean; code: string; repeat: boolean },
  insideApp: boolean,
): 'forward' | 'app' | 'browser' {
  if (event.repeat || event.altKey || event.metaKey || !event.ctrlKey || event.code !== 'KeyV') return 'forward';
  return insideApp ? 'app' : 'browser';
}

/** O que a tela do viewer pode fazer nesta sessão. Sem a permissão, o Ctrl+V segue como tecla. */
export function viewerTransferAccess(info: { access: 'view' | 'control'; clipboard: boolean; fileTransfer: boolean }) {
  const control = info.access === 'control';
  return {
    paste: control && info.clipboard,
    sendFiles: control && info.fileTransfer,
    downloadFiles: control && info.clipboard && info.fileTransfer,
  };
}

export interface PasteHooks {
  insideApp: boolean;
  onAppPaste(): void;
  /** `true` se havia texto ou arquivo. `false`: o V segue como tecla normal. */
  onBrowserPaste(data: DataTransfer): boolean;
}

type KeyInput = Pick<KeyboardEvent, 'code' | 'key' | 'repeat' | 'ctrlKey' | 'altKey' | 'metaKey' | 'preventDefault'>;

/**
 * Liga os eventos de mouse/teclado de um elemento ao envio pelo DataChannel.
 * Guarda o que está pressionado para soltar tudo se a janela perder o foco.
 * `toFrame` converte a posição do ponteiro em pixels do frame (veja `pointerToFrame`).
 * `captureSystemKeys` (só no app): com a superfície em foco, o Rust segura as teclas
 * que o Windows tomaria antes da página, como Alt+Tab e a tecla Windows.
 * A superfície pode estar na janela de um monitor desacoplado; tudo segue a janela dela.
 */
export function attachRemoteInput(
  surface: HTMLElement,
  toFrame: (clientX: number, clientY: number) => { x: number; y: number } | null,
  send: SendInput,
  paste?: PasteHooks,
  captureSystemKeys = false,
): () => void {
  const page = surface.ownerDocument;
  const view = page.defaultView ?? window;
  const pressedKeys = new Set<string>();
  const pressedButtons = new Set<MouseButton>();
  let swallowV = false;
  let browserPastePending = false;
  let pendingMove: { x: number; y: number } | null = null;
  let moveFrame: number | null = null;

  const flushMove = () => {
    moveFrame = null;
    if (pendingMove) {
      send({ type: 'mouse_move', ...pendingMove });
      pendingMove = null;
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    const pos = onScrollbar(e) ? null : toFrame(e.clientX, e.clientY);
    if (!pos) return;
    pendingMove = pos;
    // O quadro de animação da própria janela: o da principal para quando ela está minimizada.
    if (moveFrame === null) moveFrame = view.requestAnimationFrame(flushMove);
  };

  const onPointerDown = (e: PointerEvent) => {
    surface.focus();
    if (onScrollbar(e)) return;
    const button = pointerButton(e.button);
    const pos = toFrame(e.clientX, e.clientY);
    if (!button || !pos) return;
    e.preventDefault();
    surface.setPointerCapture(e.pointerId);
    // Garante que o clique acontece exatamente onde o usuário apontou.
    send({ type: 'mouse_move', ...pos });
    send({ type: 'mouse_button', button, action: 'down' });
    pressedButtons.add(button);
  };

  const onPointerUp = (e: PointerEvent) => {
    const button = pointerButton(e.button);
    if (!button) return;
    e.preventDefault();
    const pos = toFrame(e.clientX, e.clientY);
    if (pos) send({ type: 'mouse_move', ...pos });
    send({ type: 'mouse_button', button, action: 'up' });
    pressedButtons.delete(button);
    if (surface.hasPointerCapture(e.pointerId)) surface.releasePointerCapture(e.pointerId);
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    send({
      type: 'mouse_scroll',
      deltaX: wheelToLines(e.deltaX, e.deltaMode),
      deltaY: wheelToLines(e.deltaY, e.deltaMode),
    });
  };

  const onKeyDown = (e: KeyInput) => {
    if (!e.code) return;
    if (paste) {
      const kind = pasteShortcutKind(e, paste.insideApp);
      if (kind === 'app') {
        e.preventDefault();
        swallowV = true;
        paste.onAppPaste();
        return;
      }
      if (kind === 'browser') {
        // Sem preventDefault o navegador dispara `paste` com clipboardData.
        swallowV = true;
        browserPastePending = true;
        return;
      }
    }
    e.preventDefault();
    // Tecla injetada não se repete sozinha no Host: cada repetição local vira um novo "down",
    // como faz o teclado físico. Só repete o que já foi enviado (o Ctrl+V interceptado não).
    if (e.repeat && !pressedKeys.has(e.code)) return;
    send({ type: 'keyboard', code: e.code, key: e.key, action: 'down' });
    pressedKeys.add(e.code);
  };

  const onPaste = (e: ClipboardEvent) => {
    if (!browserPastePending || !paste) return;
    browserPastePending = false;
    if (e.clipboardData && paste.onBrowserPaste(e.clipboardData)) {
      e.preventDefault();
      return;
    }
    send({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'down' });
    pressedKeys.add('KeyV');
  };

  const onKeyUp = (e: KeyInput) => {
    if (!e.code) return;
    if (swallowV && e.code === 'KeyV') {
      e.preventDefault();
      swallowV = false;
      if (browserPastePending) {
        browserPastePending = false;
        send({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'down' });
        send({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'up' });
        return;
      }
      if (pressedKeys.has('KeyV')) {
        send({ type: 'keyboard', code: 'KeyV', key: 'v', action: 'up' });
        pressedKeys.delete('KeyV');
      }
      return;
    }
    e.preventDefault();
    send({ type: 'keyboard', code: e.code, key: e.key, action: 'up' });
    pressedKeys.delete(e.code);
  };

  const releaseAll = () => {
    for (const code of pressedKeys) send({ type: 'keyboard', code, key: '', action: 'up' });
    pressedKeys.clear();
    for (const button of pressedButtons) send({ type: 'mouse_button', button, action: 'up' });
    pressedButtons.clear();
  };

  const holding = (...codes: string[]) => codes.some((code) => pressedKeys.has(code));

  const onSystemKey = ({ code, action, repeat }: CapturedKey) => {
    // Com um monitor desacoplado há duas telas ouvindo o gancho; só a que está em foco envia.
    if (!page.hasFocus() || page.activeElement !== surface) return;
    const key: KeyInput = {
      code,
      key: '',
      repeat,
      ctrlKey: holding('ControlLeft', 'ControlRight'),
      altKey: holding('AltLeft', 'AltRight'),
      metaKey: holding('MetaLeft', 'MetaRight'),
      preventDefault() {},
    };
    if (action === 'down') onKeyDown(key);
    else onKeyUp(key);
  };

  // Só liga o gancho depois de ouvir o evento; antes disso as teclas seriam seguradas e perdidas.
  let stopSystemKeys: (() => void) | null = null;
  let disposed = false;
  const setSystemCapture = (active: boolean) => {
    if (stopSystemKeys) void keyboardCaptureSet(active);
  };
  if (captureSystemKeys) {
    void onCapturedKey(onSystemKey).then((unlisten) => {
      if (disposed) {
        unlisten();
        return;
      }
      stopSystemKeys = unlisten;
      if (page.activeElement === surface) setSystemCapture(true);
    });
  }

  const onFocus = () => setSystemCapture(true);
  const onBlur = () => {
    setSystemCapture(false);
    releaseAll();
  };

  const onContextMenu = (e: Event) => e.preventDefault();

  surface.addEventListener('pointermove', onPointerMove);
  surface.addEventListener('pointerdown', onPointerDown);
  surface.addEventListener('pointerup', onPointerUp);
  surface.addEventListener('wheel', onWheel, { passive: false });
  surface.addEventListener('keydown', onKeyDown);
  surface.addEventListener('keyup', onKeyUp);
  surface.addEventListener('paste', onPaste);
  surface.addEventListener('contextmenu', onContextMenu);
  surface.addEventListener('focus', onFocus);
  surface.addEventListener('blur', onBlur);
  view.addEventListener('blur', releaseAll);

  return () => {
    if (moveFrame !== null) view.cancelAnimationFrame(moveFrame);
    setSystemCapture(false);
    disposed = true;
    stopSystemKeys?.();
    releaseAll();
    surface.removeEventListener('pointermove', onPointerMove);
    surface.removeEventListener('pointerdown', onPointerDown);
    surface.removeEventListener('pointerup', onPointerUp);
    surface.removeEventListener('wheel', onWheel);
    surface.removeEventListener('keydown', onKeyDown);
    surface.removeEventListener('keyup', onKeyUp);
    surface.removeEventListener('paste', onPaste);
    surface.removeEventListener('contextmenu', onContextMenu);
    surface.removeEventListener('focus', onFocus);
    surface.removeEventListener('blur', onBlur);
    view.removeEventListener('blur', releaseAll);
  };
}
